import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'ramda/src/isEmpty';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';
import toLower from 'ramda/src/toLower';
import Button from '@components/@core/AnimatedButton';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Icon from '@components/@core/Icon';
import StepBar from '@components/@core/StepBar';
import AppActions from '~/actions/app';
import CompanyActions from '~/actions/company';
import VendorActions from '~/actions/vendor';
import { getCompanyName } from '~/services/util';
import StepOne from './StepOne';
import StepTwo from './StepTwo';

const defaultCompanyData = {
    bio: {},
    franchises: [],
    vendorStack: {},
};

const AddCompany = memo(() => {
    const dispatch = useDispatch();
    const [business, setBusiness] = useState(null);
    const status = useSelector((state) => state.manage.status);
    const navigate = useNavigate();
    const [current, setCurrent] = useState(0);
    const [companyPayload, updateCompanyPayload] = useImmer(defaultCompanyData);

    useEffect(() => {
        dispatch(VendorActions.getcategoriesRequest());
    }, []);

    const handleNext = (payload) => {
        const { bio: profile, franchises, vendorStack } = companyPayload;
        if (current < 1) {
            const { bio: profile, franchises, vendorStack } = payload;
            const disabled = !profile?.type || !profile?.name || (profile.type[0] === 1 && (!franchises[0] || isEmpty(vendorStack)));
            if (disabled) {
                toast.warn('Please Select Company type, Company Name and at least one Franchises and Vendor Stack', {
                    position: toast.POSITION.TOP_RIGHT,
                });
                return;
            }
        }
        switch (current) {
            case 0:
                updateCompanyPayload((draft) => {
                    draft.bio = payload.bio;
                    draft.franchises = payload.franchises;
                    draft.vendorStack = payload.vendorStack;
                });
                break;
            case 1:
                const reqPayload = {
                    business: {
                        name: profile.name,
                        data: {
                            profile: {
                                url: profile.url,
                                address: profile.address,
                                cell_phone: profile.primary_phone,
                                avatar: profile.avatar,
                            },
                            franchises,
                            vendors: vendorStack,
                            licences: payload.licences,
                            max_employees: payload.maxEmployee,
                        },
                    },
                };

                dispatch(CompanyActions.savebzcompanyRequest(reqPayload, (e) => setBusiness(e)));
                break;
        }

        if (current < 3) {
            setCurrent(current + 1);
        }
    };

    const handlePrev = () => {
        if (current > 0) {
            setCurrent(current - 1);
        }
    };

    const handleExit = () => {
        if (business) {
            const cpyname = business?.slug || business?.vrs?.entity?.slug || business?.vrs?.slug || `${toLower(getCompanyName(business))}`.replace(/\s/g, '-');
            navigate(`/${cpyname}/about`);
        }
    };

    const renderSteps = () => {
        switch (current) {
            case 0:
                return <StepOne onNext={handleNext} />;
            case 1:
                return <StepTwo onNext={handleNext} onPrev={handlePrev} />;
            case 2:
                if (!status.length) {
                    const modalPayload = {
                        type: 'Show Success',
                        data: {
                            before: {
                                message: 'Congratulations, your company is being added to DrivingSales. You can finish your profile set up in just a moment.',
                                autoClose: true,
                            },
                        },
                        callBack: {
                            onExit: handleExit,
                        },
                    };

                    dispatch(AppActions.modalRequest(modalPayload));
                }
                return null;
        }
    };

    return (
        <ErrorBoundary className="vdra">
            <div className="d-h-between mb-3 ms-0 ssm-l-3">
                <StepBar className="pt-0 ssp-t-3" step={current + 1} maxSteps={3} />
                <Button type="low" className="mr-0 ssm-r-5 pt-0 ssp-t-3" size="small" onClick={() => navigate(-1)}>
                    <Icon name="fas fa-arrow-left me-2" size={12} color="#376caf" />
                    Back to all companies
                </Button>
            </div>
            {renderSteps()}
        </ErrorBoundary>
    );
});

export default AddCompany;
